import { extend, get } from 'lodash';
import CollectionModule from 'client/store/modules/CollectionModule';
import DateUtil from 'client/util/DateUtil';
import MspState from 'client/components/models/MspState';
import OrganizationsApi from 'api/v2/OrganizationsApi';
import Vue from 'vue';

export const createOrganizationsCollection = (Api = OrganizationsApi) => {
  const collection = new CollectionModule(Api, '_id', 'settings.name');
  const orgCollectionModule = collection.extractModule();

  const getInitialState = () => {
    const { state } = orgCollectionModule;
    state.entities.itemDetails = {
      byId: {},
    };
    return state;
  };

  return {
    namespaced: true,

    state: getInitialState(),

    getInitialState,

    actions: {
      ...orgCollectionModule.actions,

      /**
       * @description For a given id, get the details to be displayed and update
       * the item.  This is necessary because the fetch on organizations doesn't
       * return these fields - they're only returned when specifying an id.
       */
      async getDetailsForOrganization(context, { id }) {
        try {
          const detailFields = [
            'accountsReceivable',
            'applicationsCount',
            'provider',
            'registrationLeadType',
            'registrationUseCase',
            'registrationUtmFields',
            'settings.contactEmail',
            'settings.contactName',
            'settings.passwordPolicy',
            'systemsCount',
            'systemUsersCount',
            'systemUsersStagedCount',
            'systemUsersActivatedCount',
            'systemUsersSuspendedCount',
          ];
          const details = await collection.Api.getOrganizationDetails(id, detailFields.join(' '));
          const attributes = {
            accountsReceivable: details.accountsReceivable,
            applicationsCount: details.applicationsCount,
            contactEmail: details.settings.contactEmail,
            contactName: details.settings.contactName,
            id,
            leadType: details.registrationLeadType,
            passwordPolicy: details.settings.passwordPolicy,
            provider: details.provider,
            systemsCount: details.systemsCount,
            systemUsersCount: details.systemUsersCount,
            systemUsersActivatedCount: details.systemUsersActivatedCount,
            systemUsersSuspendedCount: details.systemUsersSuspendedCount,
            systemUsersStagedCount: details.systemUsersStagedCount,
            useCase: details.registrationUseCase,
            utmFields: details.registrationUtmFields,
          };

          return context.commit('SET_ITEM_DETAILS', { id, attributes });
        } catch (error) {
          return error;
        }
      },

      /**
       * @description Override the base class method to also update the details information
       * for any open org details drawers.
       */
      refreshCollection(context, {
        item,
        skip = context.state.features.items.pagination.skip,
      } = {}) {
        return orgCollectionModule.actions
          .refreshCollection(context, { item, skip })
          .then(() => context.dispatch('refreshItemDetails'));
      },

      /**
       * @description Given the list of open org detail drawers, refresh the information
       * in case a user has edited the org in a different tab.
       */
      refreshItemDetails(context) {
        const { itemDetails } = context.state.entities;
        const ids = [];

        Object.keys(itemDetails.byId).forEach((id) => {
          ids.push(id);
        });

        if (ids.length === 0) {
          return Promise.resolve();
        }

        const tasks = ids.map(id => context.dispatch('getDetailsForOrganization', { id }));

        return Promise.all(tasks);
      },

      /**
       * @description Remove details for all drawers - used when switching contexts between
       * MSP and non-MSP.
       */
      removeAllDetails(context) {
        context.commit('REMOVE_ALL_DETAILS');
      },

      /**
       * @description If the user closes an org details drawer, remove it from the list so that
       * we are only tracking details for open drawers.
       */
      removeDetailsForOrganization(context, { id }) {
        context.commit('REMOVE_ITEM_DETAILS', { id });
      },

      /**
       * @description This method updates an item similarly to updateItem() from
       * CollectionModule.  The difference is that this updates an item that isn't
       * in the boundItems list (the MSP org list doesn't currently have the concept
       * of bound items).
       */
      updateUnboundItem(context, { id, attributes }) {
        // Write new attributes to item
        const item = extend({}, context.state.entities.items.byId[id], attributes);
        collection.Api.update(item)
          .then(() => {
            context.commit('UPDATE_ITEM', { id, attributes });
            Promise.resolve();
          })
          .catch(error => Promise.reject(error));
      },
    },

    getters: {
      ...orgCollectionModule.getters,

      getItemDetails: (state) => id => state.entities.itemDetails.byId[id] || {},

      getOrgCreatedDate(state, getters, rootState) {
        const { orgId } = rootState.SettingsModule;
        const primaryOrgId = MspState.isAnMsp ? MspState.orgId : orgId;
        const primaryOrg = getters.getItem(primaryOrgId);
        return get(primaryOrg, 'created', '');
      },

      getOrgIsOlderThanDays: (state, getters) => days => {
        const orgCreatedDate = getters.getOrgCreatedDate;
        // if orgCreatedDate is not defined, return false by default
        return Boolean(DateUtil.moreThanDaysBeforeToday(orgCreatedDate, days));
      },
    },

    mutations: {
      ...orgCollectionModule.mutations,

      REMOVE_ALL_DETAILS(state) {
        const { itemDetails } = state.entities;
        Object.keys(itemDetails.byId).forEach((id) => {
          Vue.delete(itemDetails.byId, id);
        });
      },

      REMOVE_ITEM_DETAILS(state, { id }) {
        const { itemDetails } = state.entities;
        Vue.delete(itemDetails.byId, id);
      },

      SET_ITEM_DETAILS(state, { id, attributes }) {
        const { itemDetails } = state.entities;
        Vue.set(
          itemDetails.byId,
          id,
          extend({}, itemDetails.byId[id], attributes),
        );
      },
    },
  };
};

export default createOrganizationsCollection();
